import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {PromptLibraryCard} from './PromptLibraryCard';
import {CreatePromptModal} from './CreatePromptModal';

const ROLE_FILTERS = ['all', 'media_buyer', 'analista', 'account', 'director'];
const MOMENTOS = ['dia_a_dia', 'semanal', 'antes_reunion', 'cierre_mes', 'cuando_cambio', 'cliente_nuevo'];
// El rol del usuario es estable entre clientes y sesiones → persiste global (solo modo momentos).
const ROLE_STORAGE_KEY = 'dashbo_prompt_library_role';
// "Empezá por estos 3" (D9): bloque starter de la primera visita; el dismiss persiste global.
const STARTER_DISMISSED_KEY = 'dashbo_prompt_library_starter_dismissed';
const STARTER_COUNT = 3;

function initialRole(groupByMoment) {
    if (!groupByMoment) return 'all';
    try {
        const stored = window.localStorage.getItem(ROLE_STORAGE_KEY);
        if (ROLE_FILTERS.includes(stored)) return stored;
    } catch (e) { /* localStorage roto: default */ }
    return 'all';
}

// `groupByMoment` (tab IA, ficha v2): la biblioteca se organiza en SECCIONES por momento
// (D9 del SSOT de UX — se termina el muro de 52 cards) con el rol persistente. El modo
// default (grid + dropdown de momento) queda intacto para el tab legacy con flag OFF.
export function PromptLibrarySection({clientId, groupByMoment = false}) {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [available, setAvailable] = useState([]);
    const [prompts, setPrompts] = useState([]);
    const [role, setRoleState] = useState(() => initialRole(groupByMoment));
    const [momento, setMomento] = useState('all');
    const [query, setQuery] = useState('');
    // Vista de auditoría: «qué puede recibir tu cliente» — filtra client-side por `portal_visible`.
    const [portalOnly, setPortalOnly] = useState(false);
    // Card a resaltar (fork recién creado, o el existente cuando el fork da 409).
    const [highlightId, setHighlightId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    // Prompt que se está editando. El modal es UNO solo para toda la sección (montar uno por card
    // serían 50 modales en el DOM) y se keyea por prompt, así que precarga sus campos al montarse.
    const [editingPrompt, setEditingPrompt] = useState(null);
    const [starterDismissed, setStarterDismissed] = useState(() => {
        if (!groupByMoment) return true;
        try {
            return window.localStorage.getItem(STARTER_DISMISSED_KEY) === '1';
        } catch (e) {
            return true;
        }
    });

    const dismissStarter = useCallback(() => {
        setStarterDismissed(true);
        try {
            window.localStorage.setItem(STARTER_DISMISSED_KEY, '1');
        } catch (e) { /* best-effort */ }
    }, []);

    const setRole = useCallback((r) => {
        setRoleState(r);
        if (groupByMoment) {
            try {
                window.localStorage.setItem(ROLE_STORAGE_KEY, r);
            } catch (e) { /* best-effort */ }
        }
    }, [groupByMoment]);

    // `silent`: recarga sin tumbar la lista al spinner. El refresh que sigue a un fork tiene que
    // ser silencioso — con el spinner, la sección entera se desmonta y el `scrollIntoView` del
    // resaltado busca una card que en ese momento no existe en el DOM.
    const fetchLibrary = useCallback(({silent = false} = {}) => {
        if (!silent) setLoading(true);
        const url = `${API_URL}agent/prompt-library/?agency_client_id=${clientId}`;
        return new APIClient(url).call('GET').then((data) => {
            if (!data || data.status === 'ERROR' || data.status === 'NETWORK_ERROR') {
                setError(true);
                setLoading(false);
                return;
            }
            setAvailable(data.available_ingredients || []);
            setPrompts(data.prompts || []);
            setError(false);
            setLoading(false);
        });
    }, [clientId]);

    useEffect(() => {
        fetchLibrary();
    }, [fetchLibrary]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return prompts.filter((p) => {
            const okRole = role === 'all' || p.role === role;
            const okMomento = momento === 'all' || (p.momentos || []).includes(momento);
            const okPortal = !portalOnly || p.portal_visible === true;
            const haystack = `${p.title} ${p.value_preview} ${(p.tags || []).join(' ')}`.toLowerCase();
            const okText = !q || haystack.indexOf(q) !== -1;
            return okRole && okMomento && okPortal && okText;
        });
    }, [prompts, role, momento, portalOnly, query]);

    // Los 3 starter: primeros prompts HABILITADOS que matchean el rol elegido, respetando el
    // orden del backend (habilitados primero + orden curado). Sin filtro de búsqueda a propósito
    // — es una bienvenida, no un resultado.
    const starterPrompts = useMemo(() => {
        if (!groupByMoment || starterDismissed) return [];
        return prompts
            .filter((p) => (!p.lock || p.lock.enabled) && (role === 'all' || p.role === role))
            .slice(0, STARTER_COUNT);
    }, [groupByMoment, starterDismissed, prompts, role]);

    /**
     * Las secciones por momento, con el ancla resuelta.
     *
     * Un prompt puede tener DOS momentos y entonces se renderiza dos veces. El `key` los
     * discrimina, pero el `id="pl-card-<id>"` no: dos elementos con el mismo id hacen que
     * `getElementById` (el que usa el resaltado post-fork) resuelva el que quiera. Se ancla sólo la
     * PRIMERA aparición en orden de documento — y con ella viaja el toggle de portal, así que
     * tampoco queda un switch duplicado para el mismo prompt.
     */
    const momentSections = useMemo(() => {
        if (!groupByMoment) return [];
        const anchored = new Set();
        return MOMENTOS
            .map((m) => ({momento: m, items: filtered.filter((p) => (p.momentos || []).includes(m))}))
            // Sección residual: prompts sin momento reconocido (incluye los recién creados desde
            // esta pantalla) — sin esto desaparecerían de la vista.
            .concat([{
                momento: 'otros',
                items: filtered.filter((p) => !(p.momentos || []).some((m) => MOMENTOS.includes(m))),
            }])
            .filter((section) => section.items.length > 0)
            .map((section) => ({
                momento: section.momento,
                items: section.items.map((prompt) => {
                    const anchor = !anchored.has(prompt.id);
                    if (anchor) anchored.add(prompt.id);
                    return {prompt, anchor};
                }),
            }));
    }, [groupByMoment, filtered]);

    const handleRated = useCallback((promptId, social) => {
        setPrompts((prev) => prev.map((p) => (p.id === promptId ? {...p, social} : p)));
    }, []);

    // Merge parcial sobre un prompt del state (update optimista del toggle de portal y afines).
    const handlePromptUpdated = useCallback((promptId, patch) => {
        setPrompts((prev) => prev.map((p) => (p.id === promptId ? {...p, ...patch} : p)));
    }, []);

    // Resaltar una card y llevarla a la vista. Best-effort: si los filtros activos la dejan
    // afuera del render, no hay adónde scrollear y el resaltado simplemente no se ve.
    //
    // El scroll espera al RE-RENDER: la card recién forkeada no está en el DOM en el tick en que
    // vuelve el fetch, así que se reintenta hasta que aparezca (tope ~1s). Un único timeout fijo
    // era una carrera, y la perdía cada vez que el refresh tardaba un poco más.
    const highlightCard = useCallback((promptId) => {
        setHighlightId(promptId);
        const scrollWhenPainted = (attempt = 0) => {
            const el = document.getElementById(`pl-card-${promptId}`);
            if (el && el.scrollIntoView) {
                el.scrollIntoView({behavior: 'smooth', block: 'center'});
                return;
            }
            if (attempt < 10) window.setTimeout(() => scrollWhenPainted(attempt + 1), 100);
        };
        window.setTimeout(() => scrollWhenPainted(), 0);
        window.setTimeout(() => {
            setHighlightId((current) => (current === promptId ? null : current));
        }, 3000);
    }, []);

    const handleForked = useCallback((fork) => {
        toast.success(t('prompt_library.fork_ok'));
        const refresh = fetchLibrary({silent: true});
        if (refresh && fork && fork.id) refresh.then(() => highlightCard(fork.id));
    }, [fetchLibrary, highlightCard, t]);

    const handleForkConflict = useCallback((forkId) => {
        highlightCard(forkId);
    }, [highlightCard]);

    const handleCreated = useCallback(() => {
        setCreateOpen(false);
        toast.success(t('prompt_library.created_ok'));
        fetchLibrary();
    }, [fetchLibrary, t]);

    const closeModal = useCallback(() => {
        setCreateOpen(false);
        setEditingPrompt(null);
    }, []);

    // El PUT devuelve el prompt actualizado: se mergea al instante (la card no parpadea) y detrás
    // se recarga la lista, que es la que sabe si el cambio movió al prompt de sección o de lock.
    const handleUpdated = useCallback((updated) => {
        closeModal();
        toast.success(t('prompt_library.edit_ok'));
        if (updated && updated.id) {
            setPrompts((prev) => prev.map((p) => (p.id === updated.id ? {...p, ...updated} : p)));
        }
        fetchLibrary({silent: true});
    }, [closeModal, fetchLibrary, t]);

    // El backend ya lo desactivó (soft-delete): se saca de la lista sin recargar. No vuelve.
    const handleDeactivated = useCallback((promptId) => {
        setPrompts((prev) => prev.filter((p) => p.id !== promptId));
        toast.success(t('prompt_library.deactivate_ok'));
    }, [t]);

    if (loading) {
        return (
            <div className="box-loader">
                <Spinner color="positive"/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pl-inline-error">
                <FaIcon iconName="circle-exclamation"/>
                <span>{t('prompt_library.error')}</span>
            </div>
        );
    }

    return (
        <section className="prompt-library">
            <header className="pl-header">
                <div className="pl-header-text">
                    <h3>{t('prompt_library.title')}</h3>
                    <p>{t('prompt_library.subtitle')}</p>
                </div>
                <button type="button" className="btn -alt" onClick={() => setCreateOpen(true)}>
                    <FaIcon iconName="plus"/> {t('prompt_library.create_button')}
                </button>
            </header>

            {available.length > 0 && (
                <p className="pl-context">
                    {t('prompt_library.context_prefix')}{' '}
                    {available.map((ing) => t(`prompt_library.ingredients.${ing}`, ing)).join(' · ')}
                </p>
            )}

            <div className="pl-filterbar">
                <div className="pl-search">
                    <FaIcon iconName="search"/>
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('prompt_library.search_placeholder')}
                        aria-label={t('prompt_library.search_placeholder')}
                    />
                </div>
                <div className="pl-pills" role="group" aria-label={t('prompt_library.filter_by_role')}>
                    {ROLE_FILTERS.map((r) => (
                        <button
                            key={r}
                            type="button"
                            className={`pl-pill ${role === r ? 'is-active' : ''}`}
                            aria-pressed={role === r}
                            onClick={() => setRole(r)}
                        >
                            {t(`prompt_library.roles.${r}`)}
                        </button>
                    ))}
                    {/* Auditoría de portal: qué puede recibir tu cliente. Va con los otros
                        filtros porque contesta la misma pregunta («qué estoy viendo»). */}
                    <button
                        type="button"
                        className={`pl-pill -portal ${portalOnly ? 'is-active' : ''}`}
                        aria-pressed={portalOnly}
                        onClick={() => setPortalOnly((v) => !v)}
                    >
                        <FaIcon iconName={portalOnly ? 'check' : 'eye'}/> {t('prompt_library.filter_portal_visible')}
                    </button>
                </div>
                {!groupByMoment && (
                    <select
                        className="pl-momento form-control"
                        value={momento}
                        onChange={(e) => setMomento(e.target.value)}
                        aria-label={t('prompt_library.filter_by_momento')}
                    >
                        <option value="all">{t('prompt_library.momentos.all')}</option>
                        {MOMENTOS.map((m) => (
                            <option key={m} value={m}>{t(`prompt_library.momentos.${m}`)}</option>
                        ))}
                    </select>
                )}
            </div>

            {starterPrompts.length > 0 && (
                <section className="pl-starter">
                    <div className="pl-starter-header">
                        <h4 className="pl-starter-title">{t('prompt_library.starter_title')}</h4>
                        <button
                            type="button"
                            className="pl-starter-dismiss"
                            aria-label={t('prompt_library.starter_dismiss')}
                            onClick={dismissStarter}
                        >
                            <FaIcon iconName="times"/>
                        </button>
                    </div>
                    {/* Sin `onPromptUpdated` ni `onForked`: el starter es una bienvenida, y sus 3
                        cards REPITEN prompts que están en el grid de abajo. Con los handlers, el
                        mismo prompt quedaba con dos switches de portal (mismo `name`) y dos
                        botones de duplicar. La gestión vive en el grid, que además es el único
                        que ancla. Acá queda ver y copiar. */}
                    <div className="pl-cards">
                        {starterPrompts.map((p) => (
                            <PromptLibraryCard key={`starter-${p.id}`} prompt={p}
                                               clientId={clientId} onRated={handleRated}/>
                        ))}
                    </div>
                </section>
            )}

            <p className="pl-count" aria-live="polite">
                {t('prompt_library.showing', {count: filtered.length, total: prompts.length})}
            </p>

            {filtered.length === 0 ? (
                <div className="pl-empty">{t('prompt_library.empty')}</div>
            ) : groupByMoment ? (
                momentSections.map((section) => (
                    <section key={section.momento} className="pl-moment-section">
                        <h4 className="pl-moment-title">{t(`prompt_library.momentos.${section.momento}`)}</h4>
                        <div className="pl-cards">
                            {section.items.map(({prompt: p, anchor}) => (
                                <PromptLibraryCard key={`${section.momento}-${p.id}`} prompt={p}
                                                   clientId={clientId} onRated={handleRated}
                                                   onPromptUpdated={anchor ? handlePromptUpdated : undefined}
                                                   onForked={anchor ? handleForked : undefined}
                                                   onForkConflict={handleForkConflict}
                                                   onEdit={anchor ? setEditingPrompt : undefined}
                                                   onDeactivated={anchor ? handleDeactivated : undefined}
                                                   highlighted={highlightId === p.id}
                                                   anchor={anchor}/>
                            ))}
                        </div>
                    </section>
                ))
            ) : (
                <div className="pl-cards">
                    {filtered.map((p) => (
                        <PromptLibraryCard key={p.id} prompt={p} clientId={clientId} onRated={handleRated}
                                           onPromptUpdated={handlePromptUpdated}
                                           onForked={handleForked}
                                           onForkConflict={handleForkConflict}
                                           onEdit={setEditingPrompt}
                                           onDeactivated={handleDeactivated}
                                           highlighted={highlightId === p.id}
                                           anchor/>
                    ))}
                </div>
            )}

            {/* Se monta recién al abrirse y con `key` por prompt: así el form se precarga en el
                `useState` inicial y no hace falta un efecto que re-hidrate (que pisaría lo que el
                usuario escribió cuando la lista se refresca por detrás). */}
            {(createOpen || editingPrompt) && (
                <CreatePromptModal
                    key={editingPrompt ? `edit-${editingPrompt.id}` : 'create'}
                    isOpen
                    prompt={editingPrompt}
                    toggle={closeModal}
                    clientId={clientId}
                    onCreated={handleCreated}
                    onUpdated={handleUpdated}
                />
            )}
        </section>
    );
}
